import React, { useState, useEffect, useRef } from 'react';
import { Button, Menu } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faDownload, 
  faWandMagicSparkles, 
  faMagicWandSparkles, 
  faChevronDown,
  faLock
} from '@fortawesome/free-solid-svg-icons';
import loaderStyles from '../../assets/loader.module.css';
import styles from './VideoEditor.module.css';
import { useAuth } from '../../context/Auth/AuthContext';
import { QualityLevel } from '../../hooks/useVideoProcessing';
import { useTranslation } from 'react-i18next';

interface QualityOption {
  value: QualityLevel;
  labelKey: string;
  resolution: string;
  size: string;
  descriptionKey: string;
}

// Quality option keys for translation
const qualityOptionKeys: QualityOption[] = [
  {
    value: '4k',
    labelKey: 'quality4k',
    resolution: '3840×2160',
    size: '~8GB',
    descriptionKey: 'quality4kDesc'
  },
  {
    value: '2k',
    labelKey: 'quality2k',
    resolution: '2560×1440',
    size: '~4GB',
    descriptionKey: 'quality2kDesc'
  },
  {
    value: 'hd',
    labelKey: 'qualityHd',
    resolution: '1920×1080',
    size: '~1.6GB',
    descriptionKey: 'qualityHdDesc'
  },
  {
    value: 'sd',
    labelKey: 'qualitySd',
    resolution: '1280×720',
    size: '~1GB',
    descriptionKey: 'qualitySdDesc'
  },
];

// Helper function to check if quality is allowed for a plan
const isQualityAllowed = (quality: QualityLevel, userPlan?: string): boolean => {
  const plan = userPlan || 'creator';
  const qualityHierarchy: QualityLevel[] = ['sd', 'hd', '2k', '4k'];
  
  // Define max quality for each plan
  const planMaxQuality: Record<string, QualityLevel> = {
    'creator': 'sd',
    'creator_lite': 'hd',
    'developer': 'hd',
    'creator_pro': '2k',
    'creator_studio': '4k',
    'creator_unlimited': '4k',
  };
  
  const maxQuality = planMaxQuality[plan] || 'hd';
  const qualityIndex = qualityHierarchy.indexOf(quality);
  const maxIndex = qualityHierarchy.indexOf(maxQuality);
  
  return qualityIndex <= maxIndex;
};

// Get the plan display name
const getPlanDisplayName = (plan?: string): string => {
  const planNames: Record<string, string> = {
    'creator': 'Free',
    'creator_lite': 'Creator Lite',
    'creator_pro': 'Creator Pro',
    'creator_studio': 'Creator Studio',
    'creator_unlimited': 'Creator Unlimited',
    'developer': 'Developer',
  };
  return planNames[plan || 'creator'] || 'Free';
};

interface ActionButtonsProps {
  mediaItems: any[];
  disabledBtnPendingDownload: boolean;
  isProcessing: {
    savingMedia: boolean;
    saveAndDownloadMedia: boolean;
  };
  isPreparingDownload: boolean;
  saveLabel: React.MutableRefObject<string>;
  downloadLink: React.MutableRefObject<string>;
  generatedVideoUrl: string | null;
  refLinkDownload: React.RefObject<HTMLAnchorElement>;
  onSave: () => void;
  onGenerate: (quality: QualityLevel) => void;
  onUploadMedia: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onOpenAIGenerator: () => void;
  onOpenVeoVideo: () => void;
  onOpenUpscale: () => void;
}

const ActionButtons: React.FC<ActionButtonsProps> = ({
  mediaItems,
  disabledBtnPendingDownload,
  isProcessing,
  isPreparingDownload,
  saveLabel,
  downloadLink,
  generatedVideoUrl,
  refLinkDownload,
  onSave,
  onGenerate,
  onUploadMedia,
  onOpenAIGenerator,
  onOpenVeoVideo,
  onOpenUpscale,
}) => {
  const { t } = useTranslation('videoEditor');
  const [selectedQuality, setSelectedQuality] = useState<QualityLevel>('hd');
  const [isQualityMenuOpen, setIsQualityMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth(); // Get user data from auth context

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsQualityMenuOpen(false);
      }
    };

    if (isQualityMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isQualityMenuOpen]);

  const handleGenerateWithQuality = () => {
    onGenerate(selectedQuality);
    setIsQualityMenuOpen(false);
  };

  const selectedOption = qualityOptionKeys.find(opt => opt.value === selectedQuality);

  return (
    <div style={{ display: "flex", justifyContent: '' }}>
      {/* Upload Media Button */}
      <Button className={styles.button} component="label" htmlFor="mediaUpload">
        {t('videoEditor.upload.title')}
      </Button>

      {/* AI Generator Button */}
      <Button
        className={styles.button}
        onClick={onOpenAIGenerator}
        style={{
          marginLeft: '8px',
          background: "linear-gradient(135deg, #8b5cf6, #ec4899)",
          border: "none",
          color: "white"
        }}
      >
        <FontAwesomeIcon icon={faWandMagicSparkles} style={{ marginRight: '8px' }} />
        {t('videoEditor.leftPanel.aiGenerate')}
      </Button>
      
      {/* Veo Video Button */}
      <Button
        className={styles.button}
        onClick={onOpenVeoVideo}
        style={{
          marginLeft: '8px',
          background: "linear-gradient(135deg, #10b981, #059669)",
          border: "none",
          color: "white"
        }}
      >
        🎥 VEO
      </Button>
      
      {/* Upscale Video Button */}
      <Button
        className={styles.button}
        onClick={onOpenUpscale}
        style={{
          marginLeft: '8px',
          background: "linear-gradient(135deg, #7c3aed, #a855f7)",
          border: "none",
          color: "white"
        }}
      >
        <FontAwesomeIcon icon={faMagicWandSparkles} style={{ marginRight: '8px' }} />
        {t('videoEditor.actionButtons.upscale')}
      </Button>
      
      {/* Hidden File Input */}
      <input
        id="mediaUpload"
        type="file"
        accept="video/*, image/*"
        style={{ display: 'none' }}
        onChange={onUploadMedia}
        multiple
      />

      {/* Action Buttons - Only show when media items exist */}
      {mediaItems && mediaItems.length > 0 && (
        <>
          {/* Save Button */}
          <Button
            className={styles.buttonSecondary}
            onClick={onSave}
            disabled={disabledBtnPendingDownload}
          >
            {isProcessing.savingMedia ? (
              <span style={{ display: "flex", color: "#00000078", justifyContent: "center", alignItems: "center" }}>
                {t('videoEditor.actionButtons.saving')}
                <span className={loaderStyles["lds-roller-min"]}>
                  <div></div><div></div><div></div><div></div><div></div><div></div><div></div><div></div>
                </span>
              </span>
            ) : (
              saveLabel.current || t('videoEditor.actionButtons.save')
            )}
          </Button>

          {/* Generate Media Button with Quality Dropdown */}
          <div ref={dropdownRef} style={{ display: "flex", alignItems: "center", position: 'relative' }}>
            {/* Main Generate Button */}
            <Button 
              className={styles.button} 
              onClick={handleGenerateWithQuality} 
              disabled={!mediaItems || disabledBtnPendingDownload || isPreparingDownload}
              style={{ 
                borderTopRightRadius: 0, 
                borderBottomRightRadius: 0,
                paddingRight: '16px',
                height: '2.5rem'
              }}
            >
              {isProcessing.saveAndDownloadMedia ? (
                <span style={{ display: "flex", color: "#00000078", justifyContent: "center", alignItems: "center" }}>
                  {t('videoEditor.actionButtons.processing')}
                  <span className={loaderStyles["lds-roller-min"]}>
                    <div></div><div></div><div></div><div></div><div></div><div></div><div></div><div></div>
                  </span>
                </span>
              ) : (
                <>{t('videoEditor.actionButtons.generateMedia')} ({selectedQuality.toUpperCase()})</>
              )}
            </Button>

            {/* Dropdown Button */}
            <Button
              className={styles.button}
              onClick={(e) => {
                e.stopPropagation();
                console.log('🖱️ Dropdown button clicked! Current state:', isQualityMenuOpen);
                setIsQualityMenuOpen(prev => !prev);
              }}
              disabled={!mediaItems || disabledBtnPendingDownload || isProcessing.saveAndDownloadMedia || isPreparingDownload}
              style={{
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
                borderLeft: '1px solid rgba(255,255,255,0.2)',
                paddingLeft: '8px',
                paddingRight: '8px',
                minWidth: '40px',
                height: '2.5rem'
              }}
            >
              <FontAwesomeIcon icon={faChevronDown} />
            </Button>

            {/* Custom Dropdown Menu */}
            {isQualityMenuOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '8px',
                backgroundColor: '#2a2a2a',
                border: '1px solid #444',
                borderRadius: '8px',
                padding: '8px 0',
                minWidth: '320px',
                zIndex: 999999,
                boxShadow: '0 10px 30px rgba(0,0,0,0.5)'
              }}>
                <div style={{ 
                  color: '#888', 
                  fontSize: '0.75rem', 
                  padding: '8px 16px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {t('videoEditor.quality.selectOutputQuality')}
                </div>
                
                {qualityOptionKeys.map((option) => {
                  const isAllowed = isQualityAllowed(option.value, user?.plan);
                  const isDisabled = !isAllowed;
                  
                  return (
                    <div
                      key={option.value}
                      onClick={() => {
                        if (!isDisabled) {
                          console.log('🎯 Quality selected:', option.value);
                          setSelectedQuality(option.value);
                          setIsQualityMenuOpen(false);
                        }
                      }}
                      style={{
                        padding: '14px 16px',
                        margin: '4px 8px',
                        backgroundColor: selectedQuality === option.value ? '#3a3a3a' : 'transparent',
                        color: isDisabled ? '#666' : '#fff',
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        borderRadius: '6px',
                        transition: 'all 0.2s ease',
                        border: selectedQuality === option.value ? '2px solid #10b981' : '2px solid transparent',
                        opacity: isDisabled ? 0.5 : 1,
                        position: 'relative'
                      }}
                      onMouseEnter={(e) => {
                        if (!isDisabled && selectedQuality !== option.value) {
                          e.currentTarget.style.backgroundColor = '#333';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isDisabled && selectedQuality !== option.value) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ 
                            fontWeight: 600, 
                            fontSize: '0.95rem',
                            color: isDisabled ? '#666' : (selectedQuality === option.value ? '#10b981' : '#fff')
                          }}>
                            {t(`videoEditor.quality.${option.labelKey}`)}
                            {selectedQuality === option.value && ' ✓'}
                          </span>
                          {isDisabled && (
                            <FontAwesomeIcon 
                              icon={faLock} 
                              style={{ fontSize: '0.75rem', color: '#888' }}
                            />
                          )}
                        </div>
                        <span style={{ 
                          fontSize: '0.75rem', 
                          color: isDisabled ? '#666' : '#888',
                          fontWeight: 500 
                        }}>
                          {option.size}
                        </span>
                      </div>
                      <div style={{ 
                        fontSize: '0.8rem', 
                        color: isDisabled ? '#666' : '#999',
                        marginBottom: '4px' 
                      }}>
                        {option.resolution}
                      </div>
                      <div style={{ 
                        fontSize: '0.75rem', 
                        color: isDisabled ? '#555' : '#666',
                        fontStyle: 'italic' 
                      }}>
                        {isDisabled 
                          ? t(`videoEditor.quality.upgradeTo${option.value === '4k' ? 'Studio' : option.value === '2k' ? 'Pro' : 'Lite'}`)
                          : t(`videoEditor.quality.${option.descriptionKey}`)
                        }
                      </div>
                    </div>
                  );
                })}
                
                <div style={{ 
                  borderTop: '1px solid #333',
                  marginTop: '8px',
                  paddingTop: '8px'
                }}>
                  <div style={{ 
                    color: '#10b981', 
                    fontSize: '0.75rem', 
                    padding: '8px 16px',
                    fontWeight: 600
                  }}>
                    {t('videoEditor.quality.yourPlan')}: {getPlanDisplayName(user?.plan)}
                  </div>
                  <div style={{ 
                    color: '#666', 
                    fontSize: '0.7rem', 
                    padding: '4px 16px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <span>💡</span>
                    <span>{t('videoEditor.quality.qualityTip')}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Download Badge */}
            {(downloadLink.current || generatedVideoUrl) && (
              <a
                ref={refLinkDownload}
                className={styles.videoReadyBadge}
                href={downloadLink.current || generatedVideoUrl || ''}
                target="_blank"
                rel="noopener noreferrer"
                download
                onClick={(e) => {
                  if (isPreparingDownload) {
                    e.preventDefault(); // Prevent click during preparation
                    e.stopPropagation();
                  }
                }}
                style={{ 
                  marginLeft: "8px",
                  opacity: isPreparingDownload ? 0.6 : 1,
                  cursor: isPreparingDownload ? 'not-allowed' : 'pointer',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
                title={isPreparingDownload ? 'Preparing download...' : 'Download video'}
              >
                {isPreparingDownload ? (
                  <>
                    <span className={loaderStyles["lds-roller-min"]}>
                      <div></div><div></div><div></div><div></div>
                      <div></div><div></div><div></div><div></div>
                    </span>
                    {/* <span style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                      Preparing...
                    </span> */}
                  </>
                ) : (
                  <FontAwesomeIcon icon={faDownload} />
                )}
              </a>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ActionButtons;
